import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  getUniqueRdppfTypes,
  getRestrictionsByType,
  createRestrictionsGeoJSON,
  getRestrictionColor,
  formatRestrictionTooltip,
  isRestrictionLayer,
} from '../../../../molecules/AnalysisMap/utils/restrictionLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'

interface UseRestrictionsLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle restriction polygon layers and hover tooltips
 * Creates dynamic layers for each RDPPF type present in the restrictions data
 */
export const useRestrictionsLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseRestrictionsLayersProps) => {
  // Handle restriction polygon layers
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Check if restrictions section is active
    const activeSection = (otherMapOptions as any).activeSection
    const isRestrictionsSection = activeSection === 'restrictions'
    const restrictionsData = (otherMapOptions as any).sectionData?.restrictions
    const layerStates = (otherMapOptions as any).demographicsLayerStates // Reuse same prop for consistency

    if (
      !isRestrictionsSection ||
      !restrictionsData ||
      restrictionsData.length === 0
    ) {
      // Remove restriction sources and layers if not in restrictions section
      let existingRestrictionLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingRestrictionLayers = style.layers
            .filter((layer) => isRestrictionLayer(layer.id))
            .map((layer) => layer.id)
        }
      } catch (error) {
        // Style not loaded yet, skip cleanup
        return
      }

      existingRestrictionLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(
              `Failed to remove restriction layer ${layerId}:`,
              error
            )
          }
        }
      })

      // Remove all restriction sources
      existingRestrictionLayers.forEach((layerId) => {
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          try {
            map.current!.removeSource(sourceId)
          } catch (error) {
            console.warn(
              `Failed to remove restriction source ${sourceId}:`,
              error
            )
          }
        }
      })

      return
    }

    // Restrictions section is active - create layers dynamically for each RDPPF type
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)

    rdppfTypes.forEach((rdppfType) => {
      const layerId = `restrictions-${rdppfType.id}`
      const sourceId = `${layerId}-source`

      // Get restrictions for this type
      const typeRestrictions = getRestrictionsByType(
        restrictionsData,
        rdppfType.id
      )
      const geoJson = createRestrictionsGeoJSON(typeRestrictions)

      // Get layer config (fall back to base config if specific type config doesn't exist)
      const layerConfig =
        getLayerConfig(layerId) || getLayerConfig('restrictions-base')
      const layerState = layerStates?.[layerId] || {
        enabled: layerConfig?.defaultEnabled || true,
        opacity: layerConfig?.defaultOpacity || 30,
      }

      // Add or update GeoJSON source
      if (!map.current!.getSource(sourceId)) {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: geoJson,
          // Generate feature IDs if not present - required for feature-state
          generateId: false, // We provide IDs in the GeoJSON
        })
      } else {
        // Update existing source data
        const source = map.current!.getSource(
          sourceId
        ) as mapboxgl.GeoJSONSource
        source.setData(geoJson)
      }

      // Add or update polygon fill layer
      if (!map.current!.getLayer(layerId)) {
        const fillColor = getRestrictionColor(rdppfType.id, rdppfType.name)

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-color': fillColor,
            'fill-opacity': layerState.opacity / 100,
          },
        })

        // Add outline layer for better visibility
        const outlineLayerId = `${layerId}-outline`
        if (!map.current!.getLayer(outlineLayerId)) {
          map.current!.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: sourceId,
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 2.5,
              'line-opacity': 1,
            },
          })
        }

        // Add hover layer for interactive highlight
        const hoverLayerId = `${layerId}-hover`
        if (!map.current!.getLayer(hoverLayerId)) {
          map.current!.addLayer({
            id: hoverLayerId,
            type: 'line',
            source: sourceId,
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 4,
              'line-opacity': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ],
            },
          })
        }
      } else {
        // Update existing layer
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )

        // Update outline layer
        const outlineLayerId = `${layerId}-outline`
        if (map.current!.getLayer(outlineLayerId)) {
          map.current!.setLayoutProperty(
            outlineLayerId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
        }

        // Update hover layer
        const hoverLayerId = `${layerId}-hover`
        if (map.current!.getLayer(hoverLayerId)) {
          map.current!.setLayoutProperty(
            hoverLayerId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
        }
      }
    })

    // Remove any restriction layers that are no longer needed
    const currentLayerIds = rdppfTypes.map((type) => `restrictions-${type.id}`)

    // Only get base restriction layers (not outline or hover variants)
    let existingRestrictionLayers: string[] = []
    try {
      const style = map.current.getStyle()
      if (style?.layers) {
        existingRestrictionLayers = style.layers
          .filter((layer) => {
            const id = layer.id
            return (
              id.startsWith('restrictions-') &&
              !id.endsWith('-outline') &&
              !id.endsWith('-hover')
            )
          })
          .map((layer) => layer.id)
      }
    } catch (error) {
      // Style not loaded yet, skip cleanup
      return
    }

    existingRestrictionLayers.forEach((layerId) => {
      if (!currentLayerIds.includes(layerId)) {
        // Remove base layer
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId)
        }
        // Remove outline layer
        const outlineLayerId = `${layerId}-outline`
        if (map.current!.getLayer(outlineLayerId)) {
          map.current!.removeLayer(outlineLayerId)
        }
        // Remove hover layer
        const hoverLayerId = `${layerId}-hover`
        if (map.current!.getLayer(hoverLayerId)) {
          map.current!.removeLayer(hoverLayerId)
        }
        // Remove source
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId)
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for restriction layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const restrictionsData = otherMapOptions.sectionData?.restrictions
    if (!restrictionsData || restrictionsData.length === 0) return

    // Get all enabled restriction layers
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)
    const enabledRestrictionLayers = rdppfTypes
      .map((type) => `restrictions-${type.id}`)
      .filter((layerId) => {
        const layerState = otherMapOptions.demographicsLayerStates?.[layerId]
        const isEnabled = layerState?.enabled ?? true // Default to true if state doesn't exist yet
        const layerExists = !!map.current!.getLayer(layerId)
        return isEnabled && layerExists
      })

    if (enabledRestrictionLayers.length === 0) return

    // Track currently hovered feature
    let hoveredFeatureId: string | number | null = null
    let hoveredLayerId: string | null = null

    // Handle mouse move to show tooltip and hover effect
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: enabledRestrictionLayers,
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const tooltipHtml = formatRestrictionTooltip(feature.properties)

        // Update hover state for highlighting
        if (feature.id !== undefined && feature.layer.id) {
          // Remove previous hover state
          if (hoveredFeatureId !== null && hoveredLayerId) {
            try {
              map.current!.setFeatureState(
                { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
                { hover: false }
              )
            } catch (error) {
              console.warn(
                '[Hover] Failed to remove previous hover state:',
                error
              )
            }
          }

          // Set new hover state
          // Ensure feature ID is a number for feature-state to work
          hoveredFeatureId =
            typeof feature.id === 'string'
              ? parseInt(feature.id, 10)
              : feature.id
          hoveredLayerId = feature.layer.id

          try {
            map.current!.setFeatureState(
              { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
              { hover: true }
            )
          } catch (error) {
            console.error('[Hover] Failed to set hover state:', error)
          }
        }

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              ${tooltipHtml}
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Remove hover state
        if (hoveredFeatureId !== null && hoveredLayerId) {
          map.current!.setFeatureState(
            { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
            { hover: false }
          )
          hoveredFeatureId = null
          hoveredLayerId = null
        }

        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip and clear hover state
    const handleMouseLeave = () => {
      // Remove hover state
      if (hoveredFeatureId !== null && hoveredLayerId) {
        map.current!.setFeatureState(
          { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
          { hover: false }
        )
        hoveredFeatureId = null
        hoveredLayerId = null
      }

      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners for all enabled layers
    enabledRestrictionLayers.forEach((layerId) => {
      map.current!.on('mousemove', layerId, handleMouseMove)
      map.current!.on('mouseleave', layerId, handleMouseLeave)
    })

    // Cleanup
    return () => {
      if (map.current) {
        enabledRestrictionLayers.forEach((layerId) => {
          map.current!.off('mousemove', layerId, handleMouseMove)
          map.current!.off('mouseleave', layerId, handleMouseLeave)
        })
      }
      popup.current?.remove()
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.sectionData,
    otherMapOptions.demographicsLayerStates,
  ])

  // Handle restriction clicks for auto-expand in list
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const restrictionsData = otherMapOptions.sectionData?.restrictions
    if (!restrictionsData || restrictionsData.length === 0) return

    const setSelectedItemId = otherMapOptions.setSelectedItemId
    if (!setSelectedItemId) return

    // Get all enabled restriction layers
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)
    const enabledRestrictionLayers = rdppfTypes
      .map((type) => `restrictions-${type.id}`)
      .filter((layerId) => {
        const layerState = otherMapOptions.demographicsLayerStates?.[layerId]
        const isEnabled = layerState?.enabled ?? true // Default to true if state doesn't exist yet
        const layerExists = !!map.current!.getLayer(layerId)
        return isEnabled && layerExists
      })

    if (enabledRestrictionLayers.length === 0) return

    // Handle click on restriction
    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: enabledRestrictionLayers,
      })

      if (features.length > 0) {
        const feature = features[0]
        const restrictionId = feature.properties?.id

        if (restrictionId && setSelectedItemId) {
          setSelectedItemId(restrictionId)
        }
      }
    }

    // Add click listeners for all enabled layers
    enabledRestrictionLayers.forEach((layerId) => {
      map.current!.on('click', layerId, handleClick)
    })

    // Cleanup
    return () => {
      if (map.current) {
        enabledRestrictionLayers.forEach((layerId) => {
          map.current!.off('click', layerId, handleClick)
        })
      }
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.sectionData,
    otherMapOptions.demographicsLayerStates,
    otherMapOptions.setSelectedItemId,
  ])
}
